<template>
  <VDialog class="v-confirm" :close-on-click-outside="false" :title top="50vh" :visible width="430px" @after-leave="destroy" @update:visible="onUpdateVisible">
    <Component :is="component" v-if="component" v-bind="componentProps" />
    <template v-else>{{ message }}</template>

    <template #footer>
      <VButton :label="cancelButtonLabel" :type="cancelButtonType" @click.once="cancel" />
      <VButton :label="confirmButtonLabel" :type="confirmButtonType" @click.once="confirm" />
    </template>
  </VDialog>
</template>

<script setup lang="ts" generic="TComponent extends Component">
import { type Component, onBeforeUnmount, ref } from 'vue'
import { type ComponentProps } from 'vue-component-type-helpers'

import { type VButtonType } from '../../types.ts'
import { i18nConfirm as i18n } from '../../utils/i18n.ts'
import VButton from '../VButton.vue'
import VDialog from '../VDialog.vue'

interface Props {
  cancelButtonLabel?: string
  cancelButtonType?: VButtonType
  component?: TComponent
  componentProps?: ComponentProps<TComponent>
  confirmButtonLabel?: string
  confirmButtonType?: VButtonType
  destroy: () => void
  message?: string
  reject: (reason: string) => void
  resolve: (_?: unknown) => void
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  cancelButtonLabel: () => i18n.cancel,
  cancelButtonType: 'secondary',
  component: undefined,
  componentProps: undefined,
  confirmButtonLabel: () => i18n.confirm,
  confirmButtonType: 'primary',
  message: '',
  title: '',
})

const visible = ref(false)

const element = document.activeElement as HTMLElement
element?.blur()
document.addEventListener('keydown', onKeydown)

onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))

function onKeydown({ key }: { key: string }) {
  if (key === 'Enter' || key === ' ') {
    confirm()
  } else if (key === 'Escape' || key === 'Backspace') {
    cancel()
  }
}

function onUpdateVisible(visible: boolean) {
  if (!visible) {
    cancel()
  }
}

function cancel() {
  document.removeEventListener('keydown', onKeydown)
  visible.value = false
  props.reject('cancel')
}

function confirm() {
  document.removeEventListener('keydown', onKeydown)
  visible.value = false
  props.resolve()
}

defineExpose({
  onKeydown,
  visible,
})
</script>

<style>
.v-confirm .v-dialog {
  padding: 20px;
  transform: translateY(-50%);

  .v-dialog__body,
  .v-dialog__header {
    padding-right: 35px;
  }
}
</style>
